import { Type } from '@angular/core';
import { forEach, isEmpty, isFunction } from 'lodash';
import { AnyObject, IWidgetEvent } from '@shared/types';

/* eslint-disable no-console */
const showArgumentTransformationFailureWarn = (
  data: AnyObject,
  argCode: string,
  msg: string,
): void =>
  console.warn(
    `WidgetListenerError: Argument transformation failure.
      Usually, optional chaining (ex: $event?.field) could solve the problem of null/undefined value in $event.

      Can't transform: %O,
      Using the next transformation code: '%s',
      Transformation error: %s
    `,
    data,
    argCode,
    msg,
  );

const showAbsentMethodWarn = (
  methodName: string,
  widgetInstance: AnyObject,
): void =>
  console.warn(
    `WidgetListenerError: Method %s.%s() is absent or the property with such a name isn't callable.
      Usually, it happens when you add @WidgetListener() decorator to a field that should contain a function,
      however, it isn't initialized yet for the moment of the event emitting.
      An empty stub-function might help in this case.

      Widget instance: %O`,
    widgetInstance.constructor?.name,
    methodName,
    widgetInstance,
  );

const showNoListenersWarn = (
  widgetInstance: AnyObject,
  event: IWidgetEvent,
): void =>
  console.warn(
    `WidgetListenerError: There are no listeners for '%s' event in '%s' widget.
      Usually, it happens because of a typo in the event name.
      Or after removing unneeded listeners and forgetting to delete emitting the event.

      Widget instance: %O,
      Event: %O
    `,
    event.eventName,
    event.widgetFieldPath,
    widgetInstance,
    event,
  );

const showInvalidArgCodeWarn = (argCode: string, err: Error): void =>
  console.warn(
    `WidgetListenerError: Argument code (%s) is an invalid JS expression.
      JS Parsing error: %s
      Notes:
        1. The code should be a valid JS expression (not statement).
        2. The expression is used 'as is' without any babel or webpack transformations.
        3. TypeScript isn't supported.
    `,
    argCode,
    err.message,
  );
/* eslint-enable no-console */

export const WIDGET_LISTENERS_METADATA = Symbol('Widget Listeners Metadata');

type ArgsTransformer = (eventData: AnyObject) => any[];

export interface IWidgetListenerInfo {
  methodName: string;
  argsTransformer: ArgsTransformer;
}

export interface IWidgetListenersStorage {
  [eventName: string]: IWidgetListenerInfo[];
}

const mergeStorages = (
  storage1: IWidgetListenersStorage,
  storage2?: IWidgetListenersStorage,
): IWidgetListenersStorage =>
  forEach(storage2, (methods, eventName) =>
    (storage1[eventName] || (storage1[eventName] = [])).push(...methods),
  );

const storagesCache = new Map<Type<any>, IWidgetListenersStorage>();
export const getAllWidgetListeners = (
  widgetCtor: Type<any>,
): IWidgetListenersStorage => {
  if (storagesCache.has(widgetCtor)) return storagesCache.get(widgetCtor);
  const storage: IWidgetListenersStorage = {};

  let currWidgetCtor = widgetCtor.prototype;
  while (currWidgetCtor) {
    mergeStorages(storage, currWidgetCtor[WIDGET_LISTENERS_METADATA]);
    currWidgetCtor = Object.getPrototypeOf(currWidgetCtor);
  }
  storagesCache.set(widgetCtor, storage);

  return storage;
};

const makeWidgetListenerArgsTransformer = (
  args: (string | (($event: any) => any))[] = ['$event'],
): ArgsTransformer => {
  const errorHandler = (argCode: string, data: AnyObject, err: Error): void =>
    showArgumentTransformationFailureWarn(data, argCode, err.message);

  const transformerFns = args.map((argCode) => {
    if (isFunction(argCode)) return argCode;

    let fn: Function;
    try {
      // eslint-disable-next-line
      fn = new Function(
        'errHandler',
        '$event',
        `try { return ${argCode}; } catch(e) { errHandler($event, e); }`,
      );
    } catch (e) {
      showInvalidArgCodeWarn(argCode, e);
      fn = (): void => undefined;
    }

    return fn.bind(undefined, errorHandler.bind(undefined, argCode));
  });

  return (eventData: AnyObject): any[] =>
    transformerFns.map((transformer) => transformer(eventData));
};

export const fireWidgetEventListener = (
  widgetInstance: AnyObject,
  { methodName, argsTransformer }: IWidgetListenerInfo,
  data?: AnyObject,
): void => {
  if (!isFunction(widgetInstance[methodName])) {
    return showAbsentMethodWarn(methodName, widgetInstance);
  }
  const transformedArgs = argsTransformer(data);
  widgetInstance[methodName](...transformedArgs);
};

//
// Public API
//

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function WidgetListener<T extends AnyObject = any>(
  eventName: string,
  args?: (string | (($event: T) => any))[],
): MethodDecorator {
  return (
    target: object,
    propertyKey: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    descriptor: TypedPropertyDescriptor<any>,
  ): void => {
    if (!target.hasOwnProperty(WIDGET_LISTENERS_METADATA)) {
      target[WIDGET_LISTENERS_METADATA] = {};
    }
    const storage = target[
      WIDGET_LISTENERS_METADATA
    ] as IWidgetListenersStorage;
    const byEvent = storage[eventName] || (storage[eventName] = []);
    byEvent.push({
      methodName: propertyKey,
      argsTransformer: makeWidgetListenerArgsTransformer(args),
    });
  };
}

WidgetListener.dispatch = (
  widgetInstance: AnyObject,
  event: IWidgetEvent,
): void => {
  const { constructor } = widgetInstance as { constructor: Type<any> };
  const storage: IWidgetListenersStorage = getAllWidgetListeners(constructor);

  const listenersInfo = storage[event.eventName];
  if (!listenersInfo) return showNoListenersWarn(widgetInstance, event);
  listenersInfo.forEach((info) =>
    fireWidgetEventListener(widgetInstance, info, event.data),
  );
};

/**
 * Check has the class instance at least one method decorated by {@link WidgetListener} decorator
 */
WidgetListener.hasListeners = (widgetCtor: Type<any>): boolean => {
  const storage: IWidgetListenersStorage = getAllWidgetListeners(widgetCtor);

  return !isEmpty(storage);
};
